/**
 * How a user (human or agent) is shown across the board. Kept out of the
 * component files so react-refresh stays happy and the card, modal, and
 * thread agree on the wording.
 */

export const isAgent = (user) => user?.kind === "agent";

/**
 * Any character a reader could see: not whitespace, not a format/control/
 * unassigned/separator code point, not a filler glyph that draws no ink
 * (the Hangul filler is a letter to the username validator). Kept in step
 * with backend/accounts/names.py (which says why the two may disagree on a
 * character newer than this browser's Unicode tables — safely).
 */
const INVISIBLE_CHARS = String.raw`\s\p{Cf}\p{Cc}\p{Cn}\p{Z}\u115f\u1160\u3164\uffa0\u2800`;
const VISIBLE = new RegExp(`[^${INVISIBLE_CHARS}]`, "u");
const INVISIBLE = new RegExp(`[${INVISIBLE_CHARS}]`, "gu");

/** True when a name would render as nothing (#38). */
export const isBlankName = (name) => !VISIBLE.test(name ?? "");

/**
 * A person's name to show. An account saved without one — empty, or
 * only invisible characters (#38; the server refuses new ones) — reads
 * "unnamed account #3", never an empty line nobody can tell apart.
 */
export const displayName = (user) => {
  if (!user) return "";
  return isBlankName(user.username)
    ? `unnamed account #${user.id}`
    : user.username;
};

/** "scout (agent)" / "brandon" — for selects and plain text. */
export const userLabel = (user) => {
  if (!user) return "";
  const name = displayName(user);
  return isAgent(user) ? `${name} (agent)` : name;
};

/** Two letters for an avatar chip: "br" for brandon, "ag" prefix for agents
 * would hide the name, so agents also use their username. */
export const initials = (user) =>
  isBlankName(user?.username)
    ? "?"
    : // Whole code points, after dropping what draws nothing: a leading
      // zero-width space would otherwise be the first "letter".
      Array.from(user.username.replace(INVISIBLE, "")).slice(0, 2).join("");

/** Short relative time for "claimed 5 min ago" — coarse on purpose. */
export const relativeTime = (iso, now = Date.now()) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const minutes = Math.round((now - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};
